import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { Link } from "react-router-dom";

const VIDEO_URL = "https://customer-assets.emergentagent.com/job_luxe-wellness-dc/artifacts/15sp5762_transsaviar%20%281%29.mp4";

import useMeta from "../hooks/useMeta";

export default function Home() {
  useMeta("Saviar | Soft Life Wellness, Washington DC",
    "Saviar is a luxury 21-day residential soft-life wellness experience for women in the Washington, DC area. Reset your body, identity, and nervous system.");
  return (
    <div data-testid="home-page">
      <Nav />

      {/* HERO */}
      <section className="hero" data-testid="home-hero">
        <div className="hero-bg">
          <video autoPlay muted loop playsInline data-testid="hero-video">
            <source src={VIDEO_URL} type="video/mp4" />
          </video>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg,#1C1210 0%,#2A1F15 40%,#0A0A0A 100%)", zIndex: -1 }} />
        </div>
        <div className="hero-gradient" />
        <div className="hero-content">
          <p className="eyebrow eyebrow-light">Saviar — Washington, DC</p>
          <h1>Luxury, wellness<br/>&amp; refined living.</h1>
          <p className="lead">Soft-life wellness journeys, curated gatherings, and mindset tools crafted to elevate the way you live, feel, and lead.</p>
          <div className="hero-actions">
            <Link to="/apply" className="btn btn-primary" data-testid="hero-apply-cta">Apply Now</Link>
            <Link to="/experiences" className="btn btn-outline" data-testid="hero-explore-cta">Explore Experiences</Link>
          </div>
        </div>
        <div className="hero-scroll">Scroll</div>
      </section>

      {/* JANUARY COHORT PRESALE */}
      <section className="section" style={{ background: "var(--champagne)" }} data-testid="cohort-presale">
        <div className="container-x" style={{ maxWidth: 920, textAlign: "center" }}>
          <p className="eyebrow" style={{ marginBottom: 16 }}>Founding Cohort — Now Enrolling</p>
          <h2 className="display" style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)", color: "var(--charcoal)", marginBottom: 20 }}>
            January 6 — 29, 2027. Seven seats: 3 private rooms and 4 shared spots.
          </h2>
          <p className="body-lg" style={{ maxWidth: 700, margin: "0 auto 32px" }}>
            Twenty-one days in a private residence in the Washington, DC area. Every guest has her own bed. Chef-prepared meals daily, the full cleanse and weight loss product protocol, and daily program sessions — all included.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20, maxWidth: 720, margin: "0 auto 40px" }}>
            <div style={{ padding: 28, border: "1px solid rgba(184,150,90,.3)", background: "var(--ivory)" }}>
              <p className="eyebrow" style={{ marginBottom: 8 }}>Shared Room</p>
              <p style={{ fontFamily: "var(--serif)", fontSize: "2rem", color: "var(--charcoal)", margin: 0 }}>$6,500</p>
              <p className="body-sm" style={{ marginTop: 6 }}>Own bed · shared room</p>
            </div>
            <div style={{ padding: 28, border: "1px solid var(--gold)", background: "var(--ivory)" }}>
              <p className="eyebrow" style={{ marginBottom: 8 }}>Private Room</p>
              <p style={{ fontFamily: "var(--serif)", fontSize: "2rem", color: "var(--charcoal)", margin: 0 }}>$9,500</p>
              <p className="body-sm" style={{ marginTop: 6 }}>Private room, own bed</p>
            </div>
          </div>
          <p className="body-sm" style={{ marginBottom: 28 }}>A 25% deposit reserves your seat and room tier.</p>
          <Link to="/apply" className="btn btn-primary" data-testid="cohort-apply-cta">Apply for the January Cohort</Link>
        </div>
      </section>

      {/* BOOK */}
      <section className="section" style={{ background: "var(--ivory)" }} data-testid="book-section">
        <div className="container-x">
          <div className="grid-2">
            <Reveal>
              <p className="eyebrow">The Companion Book</p>
              <div className="divider" />
              <h2 className="display" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", color: "var(--charcoal)", marginBottom: 20 }}>
                The Power to Start
              </h2>
              <p style={{ fontFamily: "var(--serif)", fontStyle: "italic", color: "var(--warm-gray)", fontSize: "1.1rem", marginBottom: 20 }}>
                Rewiring Procrastination, Depression and Anxiety — by Legacy Gamble
              </p>
              <p className="body-lg" style={{ marginBottom: 28 }}>
                A quiet manual for rewiring procrastination and starting the life you keep postponing. The frameworks that shaped the Saviar method, in your hands.
              </p>
              <a href="https://link.amazon/B0guMUZoN" target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="book-cta">Get the Book</a>
            </Reveal>
            <Reveal delay={120}>
              <div className="feature-image-wrap" style={{ aspectRatio: "3/4", background: "transparent" }}>
                <img src="/images/power-to-start-cover.jpg" alt="The Power to Start by Legacy Gamble, book cover" style={{ objectFit: "contain" }} data-testid="book-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="section-sm" style={{ background: "var(--ivory)" }}>
        <div className="container-x">
          <div className="grid-3">
            {[
              { n: "01", h: "Wellness Reset", p: "A guided 21-day transformation rooted in biochemistry, nervous system regulation, and feminine embodiment. Reset your body. Reclaim your identity." },
              { n: "02", h: "Private Society", p: "A members-only ecosystem of women devoted to softness, standards, and intentional living. Exclusive access, curated community, and a new standard for your life." },
            ].map((v, i) => (
              <Reveal key={v.n} delay={i * 120}>
                <div className="pillar">
                  <div className="eyebrow" style={{ marginBottom: 16 }}>{v.n}</div>
                  <h3>{v.h}</h3>
                  <p>{v.p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SIGNATURE OFFER */}
      <section className="section" style={{ background: "var(--ivory)" }}>
        <div className="container-x">
          <div className="grid-2" style={{ gap: 80 }}>
            <Reveal>
              <div className="feature-image-wrap">
                <img src="/images/owner-reading.jpg" alt="Saviar founder reading in a silk robe" />
                <div className="feature-tag">Signature Offer</div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <p className="eyebrow">The Soft Life Reset</p>
              <div className="divider" />
              <h2 className="display" style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", color: "var(--charcoal)", marginBottom: 24 }}>
                A 21-day luxury wellness transformation.
              </h2>
              <p className="body-lg" style={{ marginBottom: 32 }}>
                Step into a complete reset for body, identity, and nervous system. Designed to soften your stress response, refine your habits, and raise your standards to match the life you are building.
              </p>
              {[
                ["Nervous System Regulation", "Biochemistry-based protocols to release survival mode and enter embodied softness."],
                ["Identity Elevation", "Mindset tools and journaling frameworks to step into your next-level self."],
                ["Personalized Nutrition", "Curated meal frameworks aligned with hormonal health and feminine wellness cycles."],
              ].map(([h, p]) => (
                <div key={h} className="benefit-item on-light" style={{ padding: "16px 0" }}>
                  <div className="benefit-num">—</div>
                  <div>
                    <h4>{h}</h4>
                    <p>{p}</p>
                  </div>
                </div>
              ))}
              <Link to="/experiences" className="btn btn-primary" style={{ marginTop: 32 }} data-testid="signature-cta">View the Experience</Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EXPERIENCES PREVIEW */}
      <section className="section" style={{ background: "var(--champagne)" }}>
        <div className="container-x">
          <Reveal>
            <div style={{ textAlign: "center", maxWidth: 680, margin: "0 auto 48px" }}>
              <p className="eyebrow" style={{ marginBottom: 16 }}>Curated Experiences</p>
              <h2 className="display" style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", color: "var(--charcoal)" }}>
                Designed for women who live with intention.
              </h2>
            </div>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2 }}>
            {[
              { n: "01", h: "Curated Gatherings", s: "Elevated private events", img: "/images/owner-brunch.jpg" },
              { n: "02", h: "Concierge Events", s: "Bespoke, by invitation only", img: "/images/owner-wine-tour.jpg" },
            ].map((e, i) => (
              <Reveal key={e.n} delay={i * 120}>
                <div style={{ position: "relative", aspectRatio: "3/4", overflow: "hidden" }}>
                  <img src={e.img} alt={e.h} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .8s ease" }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 40%, rgba(10,10,10,.9) 100%)", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 32, color: "var(--ivory)" }}>
                    <p className="eyebrow eyebrow-light" style={{ marginBottom: 6 }}>{e.n}</p>
                    <h3 style={{ fontFamily: "var(--serif)", fontWeight: 300, fontSize: "1.7rem", margin: "0 0 6px" }}>{e.h}</h3>
                    <p style={{ color: "rgba(250,246,238,.7)", fontSize: ".88rem", margin: 0 }}>{e.s}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <Link to="/experiences" className="btn-ghost" data-testid="view-all-experiences">View All Experiences</Link>
          </div>
        </div>
      </section>

      {/* SOCIETY */}
      <section className="section dark-section">
        <div className="container-x">
          <div className="grid-2">
            <Reveal>
              <p className="eyebrow">The Saviar Society</p>
              <div className="divider" />
              <h2 className="display" style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", color: "var(--ivory)", marginBottom: 24 }}>
                More than a membership. A standard.
              </h2>
              <p className="body-lg" style={{ marginBottom: 40 }}>
                The Saviar Society is a private ecosystem for women committed to refinement, soft discipline, and intentional living. Inside: bespoke wellness guidance, exclusive events, a curated community, and the tools to build a life worth sipping on.
              </p>
              {[
                ["01", "Members-Only Events", "Access to exclusive retreats and curated gatherings."],
                ["02", "Wellness Library", "Ongoing access to Soft Life Reset protocols, guides, and masterclasses."],
                ["03", "Curated Community", "A private network of high-standard women elevating together."],
              ].map(([n, h, p]) => (
                <div key={n} className="benefit-item">
                  <div className="benefit-num">{n}</div>
                  <div><h4>{h}</h4><p>{p}</p></div>
                </div>
              ))}
              <Link to="/society" className="btn btn-primary" style={{ marginTop: 40 }} data-testid="society-cta">Explore the Society</Link>
            </Reveal>
            <Reveal delay={120}>
              <div className="feature-image-wrap">
                <img src="/images/owner-floral-mirror.jpg" alt="Saviar founder in a floral mirror" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="cta-band">
        <p className="eyebrow" style={{ marginBottom: 24 }}>Begin Your Journey</p>
        <h2>Apply for the Saviar experience.</h2>
        <p>This is not for everyone. This is for the woman who is ready.</p>
        <div className="btn-group">
          <Link to="/apply" className="btn btn-primary" data-testid="cta-apply">Apply Now</Link>
          <a href="https://calendly.com/the-soft-reset-call/discovery-call" target="_blank" rel="noopener noreferrer" className="btn btn-outline" data-testid="cta-discovery">Book a Discovery Call</a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
